/* ============================================================================
   VV Health Shop — All Supplements page behavior
   File: assets/supplements-collection.js

   Powers:
     1. Sticky category chip filtering (single-select)
     2. Sidebar filtering (multi-select: goal, form, price, in-stock)
     3. Sorting (featured/price/title/best-selling — uses data attributes)
     4. Quick add-to-cart via Shopify AJAX Cart API (/cart/add.js)
     5. Live result count + empty state
     6. Mobile filter drawer toggle

   The script is self-contained and idempotent — safe to load with `defer`.
   It uses ONLY native browser APIs (no jQuery required).
   ============================================================================ */

(function () {
  'use strict';

  const root = document.querySelector('[data-vvh-supplements]');
  if (!root) return;

  // ---------- Element handles ----------
  const grid       = root.querySelector('[data-vvh-grid]');
  const cards      = Array.from(root.querySelectorAll('[data-vvh-card]'));
  const chips      = Array.from(root.querySelectorAll('[data-vvh-chip]'));
  const filters    = Array.from(root.querySelectorAll('[data-vvh-filter]'));
  const sortSelect = root.querySelector('[data-vvh-sort]');
  const countEl    = root.querySelector('[data-vvh-count]');
  const noResults  = root.querySelector('[data-vvh-no-results]');
  const filterToggle = root.querySelector('[data-vvh-filter-toggle]');
  const filterPanel  = root.querySelector('[data-vvh-filters]');
  const clearBtns    = Array.from(root.querySelectorAll('[data-vvh-filter-clear]'));
  const searchInput  = root.querySelector('[data-vvh-search-input]');

  // ---------- State ----------
  const state = {
    chip: 'all',
    goal: new Set(),
    form: new Set(),
    price: new Set(),
    stock: new Set(),
    search: '',
    sort: 'featured',
  };

  // ---------- Helpers ----------
  function parseTags(card) {
    return (card.dataset.productTags || '').split(',').map(s => s.trim().toLowerCase());
  }

  function priceMatches(card, ranges) {
    if (ranges.size === 0) return true;
    const priceCents = parseInt(card.dataset.productPrice, 10) || 0;
    const dollars = priceCents / 100;
    for (const range of ranges) {
      const [min, max] = range.split('-').map(n => parseFloat(n));
      if (dollars >= min && dollars <= max) return true;
    }
    return false;
  }

  function applyFilters() {
    let visible = 0;
    cards.forEach(card => {
      const tags = parseTags(card);
      const inStock = card.dataset.inStock === 'true';
      const title = card.dataset.productTitle || '';

      // Chip filter
      const chipMatch = state.chip === 'all' || tags.includes(state.chip);

      // Sidebar filters
      const goalMatch = state.goal.size === 0
        || Array.from(state.goal).some(g => tags.includes(g));
      const formMatch = state.form.size === 0
        || Array.from(state.form).some(f => tags.includes(f));
      const priceMatch = priceMatches(card, state.price);
      const stockMatch = !state.stock.has('in-stock') || inStock;

      // Search
      const searchMatch = !state.search || title.includes(state.search);

      const show = chipMatch && goalMatch && formMatch && priceMatch && stockMatch && searchMatch;
      card.classList.toggle('is-hidden', !show);
      if (show) visible++;
    });

    if (countEl) {
      countEl.textContent = `${visible} ${visible === 1 ? 'product' : 'products'}`;
    }
    if (noResults) {
      noResults.hidden = visible !== 0;
    }
  }

  function applySort() {
    const visible = cards.filter(c => !c.classList.contains('is-hidden'));
    const hidden  = cards.filter(c =>  c.classList.contains('is-hidden'));

    const by = state.sort;
    visible.sort((a, b) => {
      const pa = parseInt(a.dataset.productPrice, 10) || 0;
      const pb = parseInt(b.dataset.productPrice, 10) || 0;
      const ta = a.dataset.productTitle || '';
      const tb = b.dataset.productTitle || '';
      switch (by) {
        case 'price-asc':  return pa - pb;
        case 'price-desc': return pb - pa;
        case 'title-asc':  return ta.localeCompare(tb);
        case 'best-selling': // Shopify renders best-selling order by default; preserve original DOM order
        case 'featured':
        default: return 0;
      }
    });

    [...visible, ...hidden].forEach(card => grid.appendChild(card));
  }

  function refresh() {
    applyFilters();
    applySort();
  }

  // ---------- Chip nav ----------
  chips.forEach(chip => {
    chip.addEventListener('click', () => {
      chips.forEach(c => c.classList.remove('is-active'));
      chip.classList.add('is-active');
      state.chip = chip.dataset.vvhChip;
      refresh();
    });
  });

  // ---------- Sidebar filters ----------
  filters.forEach(input => {
    input.addEventListener('change', () => {
      const kind = input.dataset.vvhFilter; // goal | form | price | stock
      if (input.checked) state[kind].add(input.value);
      else state[kind].delete(input.value);
      refresh();
    });
  });

  // ---------- Clear all ----------
  clearBtns.forEach(btn => {
    btn.addEventListener('click', () => {
      filters.forEach(input => { input.checked = false; });
      state.goal.clear();
      state.form.clear();
      state.price.clear();
      state.stock.clear();
      state.search = '';
      if (searchInput) searchInput.value = '';
      // Reset chip to "all"
      chips.forEach(c => c.classList.remove('is-active'));
      const allChip = root.querySelector('[data-vvh-chip="all"]');
      if (allChip) allChip.classList.add('is-active');
      state.chip = 'all';
      refresh();
    });
  });

  // ---------- Sort ----------
  if (sortSelect) {
    sortSelect.addEventListener('change', () => {
      state.sort = sortSelect.value;
      refresh();
    });
  }

  // ---------- Live search (filters in-page; pressing Enter submits to /search) ----------
  if (searchInput) {
    searchInput.addEventListener('input', () => {
      state.search = searchInput.value.trim().toLowerCase();
      refresh();
    });
  }

  // ---------- Mobile filter drawer ----------
  if (filterToggle && filterPanel) {
    filterToggle.addEventListener('click', () => {
      const open = filterPanel.classList.toggle('is-open');
      filterToggle.setAttribute('aria-expanded', open ? 'true' : 'false');
      document.body.style.overflow = open ? 'hidden' : '';
    });
    // Tap outside to close
    document.addEventListener('click', (e) => {
      if (!filterPanel.classList.contains('is-open')) return;
      if (filterPanel.contains(e.target) || filterToggle.contains(e.target)) return;
      filterPanel.classList.remove('is-open');
      filterToggle.setAttribute('aria-expanded', 'false');
      document.body.style.overflow = '';
    });
  }

  // ---------- Quick add-to-cart (AJAX) ----------
  root.addEventListener('submit', async (e) => {
    const form = e.target.closest('[data-vvh-add-form]');
    if (!form) return;
    e.preventDefault();

    const btn = form.querySelector('[data-vvh-add-button]');
    if (!btn || btn.classList.contains('is-loading')) return;

    btn.classList.remove('is-done');
    btn.classList.add('is-loading');

    try {
      const formData = new FormData(form);
      const res = await fetch('/cart/add.js', {
        method: 'POST',
        headers: { 'Accept': 'application/json', 'X-Requested-With': 'XMLHttpRequest' },
        body: formData,
      });

      if (!res.ok) throw new Error('Add to cart failed');
      await res.json();

      btn.classList.remove('is-loading');
      btn.classList.add('is-done');

      // Update cart count badge if your theme exposes one
      try {
        const cartRes = await fetch('/cart.js');
        const cart = await cartRes.json();
        const badge = document.querySelector('[data-cart-count], .cart-count-bubble span[aria-hidden="true"]');
        if (badge) badge.textContent = cart.item_count;

        // Fire a generic event so other parts of the theme (mini-cart drawer) can react
        document.dispatchEvent(new CustomEvent('vvh:cart:updated', { detail: cart }));
      } catch (_) { /* non-fatal */ }

      // Reset button label after 2s
      setTimeout(() => btn.classList.remove('is-done'), 2000);

    } catch (err) {
      btn.classList.remove('is-loading');
      console.error('[VVH] Add to cart error', err);
      // Fallback: submit the form normally
      form.submit();
    }
  });

  // ---------- Initial render ----------
  refresh();
})();